/**
 * useOnboardingStatus
 *
 * Tracks whether the mobile onboarding flow (push notifications + Apple Health)
 * has been completed for the current device.  Persists the flag in localStorage
 * so it survives app restarts but resets on a fresh install.
 *
 * The onboarding modal should be shown when:
 *   - The app is running inside a Capacitor native shell (iOS), AND
 *   - hasCompletedOnboarding is false
 *
 * Call markOnboardingComplete() at the end of the onboarding flow to suppress
 * future appearances.
 */

import { useCallback, useState } from "react";

const STORAGE_KEY = "healjou_onboarding_v1";

function readFlag(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function writeFlag(): void {
  try {
    localStorage.setItem(STORAGE_KEY, "1");
  } catch {
    // localStorage unavailable — treat as completed to avoid showing loop
  }
}

export function useOnboardingStatus() {
  const [hasCompletedOnboarding, setHasCompletedOnboarding] =
    useState<boolean>(readFlag);

  const markOnboardingComplete = useCallback(() => {
    writeFlag();
    setHasCompletedOnboarding(true);
  }, []);

  return { hasCompletedOnboarding, markOnboardingComplete };
}
